/**
 * Turns a World-table contact into the picture shown in the lead-status
 * panel: which outreach steps are logged, the counts behind them, and a
 * timeline an account manager can read without opening each tool.
 *
 * Everything is derived from fields already stored on the contact. Nothing
 * here writes or fetches.
 */
import { isEmailActionType } from '../shared/flowTouchpoints';
import { isAgentFailedStatus } from './outreachCompletedTag';

// "Lead" means qualified, not that anyone reached out. Disqualified and
// blacklisted are the same: the status alone is not a touch.
const AUTO_CONTACT_SKIP = new Set(['', 'not contacted', 'blacklisted', 'lead', 'disqualified']);

const ACTION_TOUCHPOINTS = new Set([
  'phone_call', 'zoom_call_scheduled', 'meet_in_person', 'online_meeting',
  'linkedin_connected', 'linkedin_message', 'loom_video', 'webinar_attended',
  'demo_given', 'proposal_sent', 'contract_sent',
]);

const EMAIL_FLOOR = {
  introduced: 1,
  'first follow-up': 2,
  'second follow-up': 3,
  'third follow-up': 4,
};

const OUTCOME_LABELS = {
  connected: 'Connected',
  meeting_scheduled: 'Meeting scheduled',
  send_info: 'Send info',
  not_interested: 'Not interested',
  no_answer: 'No answer',
  wrong_number: 'Wrong number',
};

const TOUCHPOINT_LABELS = {
  general_interest: 'General interest',
  highly_interested: 'Highly interested',
  not_now_but_friendly: 'Not now, but friendly',
  requested_info: 'Requested info',
  zoom_call_scheduled: 'Zoom call scheduled',
  meet_in_person: 'Met in person',
  online_meeting: 'Online meeting',
  phone_call: 'Phone call',
  demo_given: 'Demo given',
  proposal_sent: 'Proposal sent',
  contract_sent: 'Contract sent',
  interested_in_groupchat: 'Interested in groupchat',
  linkedin_connected: 'LinkedIn connected',
  linkedin_message: 'LinkedIn message',
  loom_video: 'Loom video sent',
  webinar_attended: 'Webinar attended',
};

const MEETING_TOUCHPOINTS = new Set(['zoom_call_scheduled', 'meet_in_person', 'online_meeting']);

const STEP_LABELS = {
  email_sent: 'Send email',
  newsletter: 'Newsletter',
  cold_email: 'Cold email',
  follow_up_email: 'Follow-up email',
  nurture_email: 'Nurture email',
  instagram_dm: 'Instagram DM',
  instagram_follow: 'Instagram follow',
  linkedin_message: 'Send LinkedIn Message',
  linkedin_connect: 'LinkedIn Connect',
  whatsapp: 'WhatsApp',
  phone_call: 'Phone call',
  sales_meeting: 'Meeting',
  delay: 'Wait',
  condition: 'Decision',
  end: 'End',
  log_notes: 'Log notes',
  auto_send_email: 'Send email',
  auto_check_reply: 'Check reply',
};

const ACTIVITY_LABELS = {
  call: 'Call',
  voicemail: 'Voicemail',
  meeting: 'Meeting',
  email: 'Email note',
  note: 'Note',
};

export const STATUS_STEPS = [
  { id: 'contacted', label: 'Contacted', icon: 'fa-bullseye' },
  { id: 'email', label: 'Email', icon: 'fa-envelope' },
  { id: 'followup', label: 'Follow-up', icon: 'fa-reply' },
  { id: 'call', label: 'Call', icon: 'fa-phone' },
  { id: 'linkedin', label: 'LinkedIn Connect', icon: 'fa-brands fa-linkedin-in' },
  { id: 'reply', label: 'Reply', icon: 'fa-comment' },
  { id: 'meeting', label: 'Meeting', icon: 'fa-handshake' },
];

export function toEpoch(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') {
      const t = value.toDate()?.getTime?.();
      return Number.isFinite(t) ? t : null;
    }
    const seconds = typeof value.seconds === 'number' ? value.seconds : value._seconds;
    return typeof seconds === 'number' ? seconds * 1000 : null;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) return null;
    return value < 1e12 ? Math.round(value * 1000) : Math.round(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^\d+$/.test(trimmed)) return toEpoch(Number(trimmed));
    const t = Date.parse(trimmed);
    return Number.isFinite(t) ? t : null;
  }
  return null;
}

export function parseFollowers(raw) {
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return Math.round(raw);
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) return null;
  const suffixed = trimmed.match(/^([\d.,]+)\s*([km])$/);
  if (suffixed) {
    const n = parseFloat(suffixed[1].replace(',', '.'));
    if (!Number.isFinite(n)) return null;
    return Math.round(n * (suffixed[2] === 'm' ? 1_000_000 : 1_000));
  }
  const digits = parseInt(trimmed.replace(/[^\d]/g, ''), 10);
  return Number.isFinite(digits) && digits > 0 ? digits : null;
}

export function formatFollowers(count) {
  if (count == null || count <= 0) return null;
  if (count >= 1_000_000) return `${trimOne(count / 1_000_000)}M`;
  if (count >= 1_000) return `${trimOne(count / 1_000)}K`;
  return String(count);
}

export function relativeLabel(ms, now = Date.now()) {
  if (!ms) return null;
  const days = Math.floor((now - ms) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return months === 1 ? '1 month ago' : `${months} months ago`;
  const years = Math.floor(days / 365);
  return years === 1 ? '1 year ago' : `${years} years ago`;
}

function trimOne(n) {
  return n.toFixed(1).replace(/\.0$/, '');
}

function cleanStatus(value) {
  return String(value || '').trim().toLowerCase();
}

function noteList(raw) {
  const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
  return list.map((entry, idx) => {
    if (typeof entry === 'string') {
      const text = entry.trim();
      return text ? { id: `legacy_note_${idx}`, type: 'private', activity: 'note', text, createdAt: null } : null;
    }
    if (entry && typeof entry === 'object') return entry;
    return null;
  }).filter(Boolean);
}

function peopleList(raw) {
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed.filter((p) => p && typeof p === 'object') : [];
  } catch (e) {
    return [];
  }
}

function touchpointEntries(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
  return Object.entries(raw)
    .filter(([, value]) => value !== false && value != null && value !== '' && value !== 0)
    .map(([key, value]) => ({
      key,
      label: TOUCHPOINT_LABELS[key] || key.replace(/^custom_/, '').replace(/_/g, ' '),
      count: typeof value === 'number' ? value : 1,
    }));
}

export function mailbox(value) {
  const raw = String(value || '').trim();
  const bracket = raw.match(/<([^<>\s]+@[^<>\s]+)>/);
  const email = (bracket ? bracket[1] : raw).trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : '';
}

function collectEmails(contact) {
  const raw = [];
  if (Array.isArray(contact.emailHistory)) raw.push(...contact.emailHistory);
  if (contact.lastEmailSent && typeof contact.lastEmailSent === 'object') raw.push(contact.lastEmailSent);
  if (contact.lead_last_email && typeof contact.lead_last_email === 'object') raw.push(contact.lead_last_email);

  const emails = [];
  raw.forEach((record) => {
    if (!record || typeof record !== 'object') return;
    const at = toEpoch(record.timestamp || record.sentAt || record.date);
    const subject = record.subjectLineText || record.subject || '';
    const duplicate = emails.find((email) => {
      if (at && email.at && Math.abs(email.at - at) < 120000) {
        return !subject || !email.subject || subject === email.subject;
      }
      return !at && !email.at && !!subject && subject === email.subject;
    });
    const from = mailbox(record.senderEmail || record.from);
    if (duplicate) {
      if (!duplicate.subject && subject) duplicate.subject = subject;
      if (!duplicate.template && record.templateName) duplicate.template = record.templateName;
      if (!duplicate.phase && (record.phase || record.status)) duplicate.phase = record.phase || record.status;
      if (!duplicate.from && from) duplicate.from = from;
      if (!duplicate.at && at) duplicate.at = at;
      return;
    }
    emails.push({
      at,
      subject,
      template: record.templateName || '',
      phase: record.phase || record.status || '',
      from,
    });
  });
  return emails.sort((a, b) => (a.at || 0) - (b.at || 0));
}

function flowStepLabel(step, idx) {
  if (!step) return `Step ${idx + 1}`;
  if (step.label) return step.label;
  if (step.nodeType === 'condition' || step.type === 'condition') {
    return step.customQuestion || 'Decision';
  }
  const key = step.type || step.nodeType || '';
  return STEP_LABELS[key] || (key ? key.replace(/_/g, ' ') : `Step ${idx + 1}`);
}

function isEmailStep(step) {
  if (!step) return false;
  return isEmailActionType(step.type) || isEmailActionType(step.nodeType)
    || step.nodeType === 'auto_send_email' || step.type === 'auto_send_email';
}

function isCallStep(step) {
  const type = String(step?.type || step?.nodeType || '').toLowerCase();
  return type === 'phone_call' || type.includes('phone');
}

function isMeetingStep(step) {
  return String(step?.type || '').toLowerCase().includes('meeting');
}

function isLinkedInStep(step) {
  return `${step?.type || ''} ${step?.nodeType || ''}`.toLowerCase().includes('linkedin');
}

function isLinkedInConnectStep(step) {
  return `${step?.type || ''}`.toLowerCase().includes('linkedin_connect');
}

function isLinkedInMessageStep(step) {
  return /linkedin_(message|dm)/.test(`${step?.type || ''}`.toLowerCase());
}

function instagramHandle(raw) {
  const value = String(raw || '').trim();
  if (!value) return '';
  const fromUrl = value.match(/instagram\.com\/([^/?#]+)/i);
  const handle = (fromUrl ? fromUrl[1] : value).replace(/^@/, '');
  return handle && handle.toLowerCase() !== 'instagram.com' ? handle : '';
}

/** Same label the table badge shows, so the panel matches the cell that was clicked. */
function tableBadge(contact, status) {
  if (status === 'In Outreach') return { label: 'In Outreach', color: null, isTag: false };
  if (isAgentFailedStatus(status)) return { label: status, color: null, isTag: false };
  const tags = contact.tags && typeof contact.tags === 'object' ? Object.values(contact.tags) : [];
  const last = tags.length ? tags[tags.length - 1] : null;
  if (last && last.tag) return { label: last.tag, color: last.color || null, isTag: true };
  let label = status;
  if (label.includes('Follow-up') || label.includes('Follow-Up')) {
    label = label
      .replace(/First|Second|Third/, (match) => ({ First: '1.', Second: '2.', Third: '3.' }[match]))
      .replace('Follow-Up', 'Follow-up');
  }
  return { label, color: null, isTag: false };
}

function readFlow(state, flows) {
  if (!state || !state.flowId || state.currentStepIndex < 0) return null;
  const def = (flows || []).find((flow) => flow.id === state.flowId) || null;
  const history = state.stepHistory || {};
  const defined = Array.isArray(def?.steps) ? def.steps : [];
  const historyIndexes = Object.keys(history)
    .map((key) => parseInt(key, 10))
    .filter((index) => !Number.isNaN(index));
  const historySpan = historyIndexes.length ? Math.max(...historyIndexes) + 1 : 0;
  const total = Math.max(defined.length, historySpan, (state.currentStepIndex || 0) + 1);
  const steps = [];
  for (let idx = 0; idx < total; idx += 1) {
    const step = defined[idx] || null;
    const entry = history[idx] || history[String(idx)] || null;
    steps.push({
      label: flowStepLabel(step, idx),
      done: !!entry,
      current: state.currentStepIndex === idx && !entry,
      at: toEpoch(entry?.completedAt),
      email: isEmailStep(step),
      call: isCallStep(step),
      meeting: isMeetingStep(step),
      linkedin: isLinkedInStep(step),
      linkedinConnect: isLinkedInConnectStep(step),
      linkedinMessage: isLinkedInMessageStep(step),
    });
  }
  return {
    name: state.flowName || def?.name || 'Flow',
    current: state.currentStepIndex,
    total: steps.length,
    doneCount: steps.filter((step) => step.done).length,
    startedAt: toEpoch(state.startedAt),
    steps,
  };
}

function personName(contact, people) {
  const named = [contact.lead_firstname, contact.lead_lastname].filter(Boolean).join(' ').trim();
  if (named) return named;
  const fromPeople = people.find((person) => person.name && String(person.name).trim());
  return fromPeople ? String(fromPeople.name).trim() : '';
}

function visibleTag(contact) {
  const tags = contact.tags;
  if (!tags || typeof tags !== 'object') return null;
  const values = Object.values(tags).filter((tag) => tag && tag.tag && tag.id !== 'favorite');
  return values.length ? values[values.length - 1].tag : null;
}

function touchCount(entries, key) {
  const hit = entries.find((entry) => entry.key === key);
  return hit ? Math.max(1, hit.count || 1) : 0;
}

/**
 * @param {object} contact World DB contact
 * @param {{ flows?: object[], now?: number }} [options]
 */
export function buildLeadStatus(contact, options = {}) {
  const now = options.now || Date.now();
  const c = contact || {};
  const notes = noteList(c.lead_notes);
  const people = peopleList(c.lead_decision_makers);
  const touchpoints = touchpointEntries(c.touchpoints);
  const emails = collectEmails(c);
  const status = c.lead_contacted || c.lead_status || 'Not Contacted';
  const statusKey = cleanStatus(status);
  const flow = readFlow(c.warmupFlow, options.flows) || readFlow(c.activeFlow, options.flows);

  const callNotes = notes.filter((note) => note.activity === 'call' || note.activity === 'voicemail');
  const meetingNotes = notes.filter((note) => note.activity === 'meeting' || note.outcome === 'meeting_scheduled');
  const callFromTouch = callNotes.length === 0 ? touchCount(touchpoints, 'phone_call') : 0;
  const callFromFlow = callNotes.length === 0 && callFromTouch === 0 && !!flow?.steps.some((step) => step.done && step.call);
  const calls = callNotes.length + callFromTouch + (callFromFlow ? 1 : 0);

  const meetingFromTouch = meetingNotes.length === 0
    ? touchpoints.filter((entry) => MEETING_TOUCHPOINTS.has(entry.key)).reduce((sum, entry) => sum + entry.count, 0)
    : 0;
  const meetingFromFlow = meetingNotes.length === 0 && meetingFromTouch === 0 && !!flow?.steps.some((step) => step.done && step.meeting);
  const meetings = meetingNotes.length + meetingFromTouch + (meetingFromFlow ? 1 : 0);

  const emailFloor = EMAIL_FLOOR[statusKey] || 0;
  const emailStepsDone = flow?.steps.filter((step) => step.done && step.email).length || 0;
  // The pipeline status is itself a send count (Introduced = 1, 3rd follow-up = 4).
  // Only the latest mail is always stored, so a single lead_last_email must not
  // hide the earlier sends the status already records.
  const recordedEmails = emails.length;
  const emailsSent = Math.max(recordedEmails, emailFloor, recordedEmails === 0 ? emailStepsDone : 0);
  const emailsInferred = emailsSent > recordedEmails;

  const replyAt = toEpoch(c.lead_last_reply_timestamp);
  const replied = c.lead_reply === true || c.lead_has_replied === true || !!replyAt
    || Object.values(c.tags || {}).some((tag) => tag && /repl/i.test(tag.tag || tag.id || ''));

  const followers = parseFollowers(c.lead_instagram_followers);
  const followersCheckedAt = toEpoch(c.researchIntelligence?.instagramMeta?.fetchedAt);
  const lastEmailAt = emails.reduce((latest, email) => (email.at && (!latest || email.at > latest) ? email.at : latest), null)
    || toEpoch(c.lead_last_email?.timestamp)
    || toEpoch(c.lastEmailSent?.sentAt)
    || toEpoch(c.lastEmailSent?.timestamp);
  const timeline = [];
  const logged = [];

  const addedAt = toEpoch(c.timestamp) || toEpoch(c.created_at) || toEpoch(c.lead_added_at) || toEpoch(c.lead_since);
  if (addedAt) {
    timeline.push({ id: 'added', at: addedAt, kind: 'added', icon: 'fa-plus', title: 'Added to targets', detail: '' });
  }

  emails.forEach((email, idx) => {
    if (!email.at && !email.subject) return;
    const title = email.subject || (idx === 0 ? 'Email sent' : 'Follow-up sent');
    const detail = [email.template, email.phase].filter(Boolean).join(' · ');
    const from = email.from || '';
    if (email.at) {
      timeline.push({ id: `email-${idx}`, at: email.at, kind: 'email', icon: 'fa-envelope', title, detail, from });
    } else {
      logged.push({ id: `email-${idx}`, kind: 'email', icon: 'fa-envelope', title, detail, from });
    }
  });

  if (emailsInferred && emails.length === 0 && emailStepsDone === 0) {
    const at = toEpoch(c.lead_last_email?.timestamp) || toEpoch(c.lead_contacted_timestamp) || toEpoch(c.last_contact_date);
    const item = {
      id: 'email-inferred',
      at,
      kind: 'email',
      icon: 'fa-envelope',
      title: emailsSent > 1 ? `${emailsSent} emails reached this status` : 'Email sent',
      detail: status,
    };
    if (at) timeline.push(item);
    else logged.push(item);
  }

  notes.forEach((note, idx) => {
    const activity = note.activity || 'note';
    const at = toEpoch(note.createdAt);
    const outcome = OUTCOME_LABELS[note.outcome] || '';
    const title = ACTIVITY_LABELS[activity] || 'Note';
    const detail = [outcome, (note.text || '').trim()].filter(Boolean).join(' — ');
    const kind = activity === 'voicemail' ? 'call' : activity;
    const icon = activity === 'call' || activity === 'voicemail' ? 'fa-phone'
      : activity === 'meeting' ? 'fa-handshake'
        : activity === 'email' ? 'fa-envelope'
          : 'fa-sticky-note';
    const item = { id: note.id || `note-${idx}`, at, kind, icon, title, detail };
    if (at) timeline.push(item);
    else if (detail || activity !== 'note') logged.push(item);
  });

  if (replied) {
    const item = { id: 'reply', at: replyAt, kind: 'reply', icon: 'fa-comment', title: 'Reply received', detail: '' };
    if (replyAt) timeline.push(item);
    else logged.push(item);
  }

  people.forEach((person, idx) => {
    if (!person.contacted) return;
    const at = toEpoch(person.contactedAt);
    const item = {
      id: `person-${idx}`,
      at,
      kind: 'person',
      icon: 'fa-user',
      title: `${person.name || 'Decision maker'} contacted`,
      detail: person.email || person.role || person.position || '',
    };
    if (at) timeline.push(item);
    else logged.push(item);
  });

  if (flow?.startedAt) {
    timeline.push({
      id: 'flow-start',
      at: flow.startedAt,
      kind: 'flow',
      icon: 'fa-share-alt',
      title: `Flow started · ${flow.name}`,
      detail: '',
    });
  }
  const linkedinLogs = (Array.isArray(c.dmInteractions) ? c.dmInteractions : [])
    .filter((item) => item && (
      String(item.channel || '').toLowerCase() === 'linkedin' || /linkedin/i.test(item.stepLabel || '')
    ))
    .map((item, idx) => ({
      id: item.id || `li-${idx}`,
      at: toEpoch(item.at),
      name: (item.name || '').trim(),
      role: item.role || '',
      stepLabel: item.stepLabel || 'LinkedIn',
      url: item.linkedinUrl || '',
    }));

  linkedinLogs.forEach((log) => {
    const item = {
      id: `li-${log.id}`,
      at: log.at,
      kind: 'linkedin',
      icon: 'fa-brands fa-linkedin-in',
      title: log.stepLabel,
      detail: [log.name, log.role].filter(Boolean).join(' · '),
    };
    if (log.at) timeline.push(item);
    else logged.push(item);
  });

  (flow?.steps || []).forEach((step, idx) => {
    if (!step.done || !step.at) return;
    const alreadyListed = timeline.some((item) => item.at && Math.abs(item.at - step.at) < 120000 && (
      (step.email && item.kind === 'email')
      || (step.call && item.kind === 'call')
      || (step.meeting && item.kind === 'meeting')
      || (step.linkedin && item.kind === 'linkedin')
    ));
    if (alreadyListed) return;
    timeline.push({
      id: `flow-step-${idx}`,
      at: step.at,
      kind: 'flow',
      icon: 'fa-check',
      title: step.label,
      detail: flow.name,
    });
  });

  const pastFlows = []
    .concat(Array.isArray(c.warmupFlowHistory) ? c.warmupFlowHistory : [])
    .concat(Array.isArray(c.flowHistory) ? c.flowHistory : []);
  pastFlows.forEach((entry, idx) => {
    const at = toEpoch(entry.endedAt || entry.startedAt);
    if (!at) return;
    const done = entry.stepsCompleted ?? entry.lastStepIndex;
    timeline.push({
      id: `flow-past-${idx}`,
      at,
      kind: 'flow',
      icon: 'fa-flag-checkered',
      title: `Flow ${entry.reason || entry.status || 'ended'} · ${entry.flowName || 'Flow'}`,
      detail: Number.isFinite(done) ? `${done} steps` : '',
    });
  });

  const jobs = c.outreachHistory && typeof c.outreachHistory === 'object' ? Object.values(c.outreachHistory) : [];
  jobs.forEach((job, idx) => {
    if (!job || typeof job !== 'object') return;
    const at = toEpoch(job.at);
    const title = `Outreach ${job.status || 'updated'}`;
    const item = { id: `job-${idx}`, at, kind: 'outreach', icon: 'fa-bullhorn', title, detail: job.reason || '' };
    if (at) timeline.push(item);
    else logged.push(item);
  });

  const contactedAt = toEpoch(c.lead_contacted_timestamp);
  if (contactedAt && !timeline.some((item) => item.at && Math.abs(item.at - contactedAt) < 120000 && item.kind !== 'added')) {
    timeline.push({
      id: 'status-set',
      at: contactedAt,
      kind: 'status',
      icon: 'fa-bullseye',
      title: `Status set to ${status}`,
      detail: '',
    });
  }

  timeline.sort((a, b) => b.at - a.at);

  const contactMoments = timeline
    .filter((item) => ['email', 'call', 'meeting', 'reply', 'person', 'status', 'outreach', 'linkedin'].includes(item.kind))
    .map((item) => item.at);
  const explicitLast = toEpoch(c.last_contact_date);
  if (explicitLast) contactMoments.push(explicitLast);
  const moments = contactMoments.filter(Boolean);
  const firstContactAt = moments.length ? Math.min(...moments) : null;
  const lastContactAt = moments.length ? Math.max(...moments) : null;

  const connectLogs = linkedinLogs.filter((log) => /connect/i.test(log.stepLabel));
  const messageLogs = linkedinLogs.filter((log) => /message|\bdm\b/i.test(log.stepLabel));
  const linkedinSteps = flow?.steps.filter((step) => step.done && step.linkedin) || [];
  const linkedin = {
    connects: Math.max(
      connectLogs.length,
      touchCount(touchpoints, 'linkedin_connected'),
      linkedinSteps.filter((step) => step.linkedinConnect).length,
    ),
    messages: Math.max(
      messageLogs.length,
      touchCount(touchpoints, 'linkedin_message'),
      linkedinSteps.filter((step) => step.linkedinMessage).length,
    ),
    logs: [...linkedinLogs].sort((a, b) => (b.at || 0) - (a.at || 0)),
    inactive: c.lead_linkedin_inactive === true,
    lastAt: linkedinLogs.reduce((latest, log) => (log.at && (!latest || log.at > latest) ? log.at : latest), null)
      || linkedinSteps.reduce((latest, step) => (step.at && step.linkedin && (!latest || step.at > latest) ? step.at : latest), null),
    lastName: [...linkedinLogs].sort((a, b) => (b.at || 0) - (a.at || 0)).find((log) => log.name)?.name || '',
  };
  const coveredFlowLogs = linkedinSteps
    .filter((step) => !linkedinLogs.some((log) => log.at && step.at && Math.abs(log.at - step.at) < 120000))
    .map((step, idx) => ({
      id: `flow-li-${idx}`,
      at: step.at,
      name: '',
      role: '',
      stepLabel: step.label,
      url: '',
    }));
  linkedin.logs = [...linkedin.logs, ...coveredFlowLogs].sort((a, b) => (b.at || 0) - (a.at || 0));
  linkedin.done = linkedin.connects > 0 || linkedin.messages > 0 || linkedin.logs.length > 0 || linkedinSteps.length > 0;

  const statusSaysContacted = !AUTO_CONTACT_SKIP.has(statusKey);
  const actionTouch = touchpoints.some((entry) => ACTION_TOUCHPOINTS.has(entry.key));
  const contacted = statusSaysContacted || moments.length > 0 || emailsSent > 0 || calls > 0
    || meetings > 0 || replied || actionTouch || linkedin.done || (flow?.doneCount > 0)
    || people.some((person) => person.contacted) || jobs.length > 0;

  const latestCall = [...callNotes].sort((a, b) => (toEpoch(b.createdAt) || 0) - (toEpoch(a.createdAt) || 0))[0];
  const callDetail = calls === 0
    ? 'No call note yet'
    : latestCall
      ? [OUTCOME_LABELS[latestCall.outcome], relativeLabel(toEpoch(latestCall.createdAt), now)].filter(Boolean).join(' · ') || `${calls} logged`
      : `${calls} logged`;

  const linkedinDetail = !linkedin.done
    ? (linkedin.inactive ? 'Marked inactive' : 'No connect yet')
    : [
      linkedin.connects ? `${linkedin.connects} connect${linkedin.connects === 1 ? '' : 's'}` : '',
      linkedin.messages ? `${linkedin.messages} message${linkedin.messages === 1 ? '' : 's'}` : '',
      linkedin.lastName,
      relativeLabel(linkedin.lastAt, now),
    ].filter(Boolean).join(' · ');

  const contactedDetail = !contacted
    ? 'Not contacted yet'
    : statusSaysContacted
      ? status
      : calls > 0
        ? 'Call logged'
        : emailsSent > 0
          ? 'Email sent'
          : meetings > 0
            ? 'Meeting logged'
            : replied
              ? 'Reply received'
              : linkedin.done
                ? 'LinkedIn logged'
                : 'Reached';

  const stepState = {
    contacted: { done: contacted, detail: contactedDetail },
    email: {
      done: emailsSent > 0,
      detail: emailsSent > 0
        ? `${emailsSent} sent${emailsInferred ? ' · from status' : ''}`
        : 'No email sent',
    },
    followup: {
      done: emailsSent >= 2 || statusKey.includes('follow-up') || statusKey.includes('follow up'),
      detail: emailsSent >= 2 ? `${emailsSent - 1} after the first` : 'No follow-up yet',
    },
    call: { done: calls > 0, detail: callDetail },
    linkedin: { done: linkedin.done, detail: linkedinDetail },
    reply: {
      done: replied,
      detail: replied ? (relativeLabel(replyAt, now) || 'Reply logged') : 'No reply yet',
    },
    meeting: {
      done: meetings > 0,
      detail: meetings > 0 ? `${meetings} logged` : 'No meeting yet',
    },
  };

  const steps = STATUS_STEPS.map((step) => ({ ...step, ...stepState[step.id] }));
  const doneCount = steps.filter((step) => step.done).length;

  return {
    company: c.business_name || c.lead_company || personName(c, people) || 'Untitled contact',
    person: personName(c, people),
    email: c.lead_email || '',
    phone: c.lead_telephone || c.lead_phone_whatsapp || '',
    instagram: instagramHandle(c.lead_instagram || c.lead_instagram_username),
    status,
    badge: tableBadge(c, status),
    tag: visibleTag(c),
    followers,
    followersLabel: formatFollowers(followers),
    followersCheckedAt,
    lastEmailAt,
    prepared: c.has_prepared_email === true,
    openTasks: Array.isArray(c.open_task_assignees) ? c.open_task_assignees.length : 0,
    firstContactAt,
    lastContactAt,
    emailsSent,
    emailsInferred,
    calls,
    meetings,
    linkedin,
    replied,
    notesCount: notes.length,
    steps,
    doneCount,
    totalSteps: steps.length,
    percent: Math.round((doneCount / steps.length) * 100),
    timeline,
    logged,
    flow,
    people: people.map((person) => ({
      name: person.name || 'Unnamed',
      role: person.role || person.position || '',
      email: person.email || '',
      contacted: !!person.contacted,
      contactedAt: toEpoch(person.contactedAt),
    })),
    touchpoints,
    recentNotes: [...notes]
      .sort((a, b) => (toEpoch(b.createdAt) || 0) - (toEpoch(a.createdAt) || 0))
      .slice(0, 3)
      .map((note) => ({
        id: note.id,
        activity: note.activity || 'note',
        title: ACTIVITY_LABELS[note.activity] || 'Note',
        outcome: OUTCOME_LABELS[note.outcome] || '',
        text: (note.text || '').trim(),
        at: toEpoch(note.createdAt),
      })),
  };
}

const NEXT_ACTIONS = {
  email: { id: 'email', label: 'Send email', icon: 'fa fa-envelope' },
  followup: { id: 'followup', label: 'Send follow-up', icon: 'fa fa-reply' },
  call: { id: 'call', label: 'Log call', icon: 'fa fa-phone' },
  linkedin: { id: 'linkedin', label: 'LinkedIn Connect', icon: 'fa-brands fa-linkedin-in' },
  meeting: { id: 'meeting', label: 'Log meeting', icon: 'fa fa-handshake' },
};

/** The two next steps an account manager can actually start. A reply waits on the contact. */
export function nextLeadActions(snap) {
  if (!snap) return [];
  const done = (id) => snap.steps.some((step) => step.id === id && step.done);
  const queue = [];
  if (snap.email && !done('email')) queue.push('email');
  else if (snap.email && !done('followup')) queue.push('followup');
  if (!done('call')) queue.push('call');
  if (!done('linkedin') && !snap.linkedin?.inactive) queue.push('linkedin');
  if (!done('meeting')) queue.push('meeting');
  return queue.slice(0, 2).map((id) => NEXT_ACTIONS[id]);
}
